import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {isFIWFeatureEnabled} from 'common/report/util/fast-report-iteration';

import {SaveModes} from '../types';
import css from './index.inline.styl';
import {template} from './template';

interface BoardsSaveButtonAttrs {
  'selected-dashboard-id': number;
  'user-id': number;
  'can-save-existing': boolean;
  'can-save-as-new': boolean;
  tooltip?: string;
  'align-right'?: boolean;
  'search-placeholder': string;
  'is-saving'?: boolean;
  'has-unsaved-changes': boolean;
  'has-existing-report': boolean;
  'show-save-spinner': boolean;
}

interface BoardsSaveButtonJsxProps {
  attrs?: BoardsSaveButtonAttrs;
  props?: {
    dashboardController: DashboardSelectorController;
    recentDashboardIds?: number[];
  };
  on?: {
    save: (ev?: CustomEvent) => void;
  };
}

interface BoardsSaveButtonState {
  isOpen: boolean;
  recentDashboardIds: number[];
  pendingSaveMode?: SaveModes;
}

export class BoardsSaveButton extends Component<
  BoardsSaveButtonState,
  BoardsSaveButtonAttrs,
  any,
  any,
  IronContextRegistry
> {
  private _dashboardController: DashboardSelectorController;

  static get attrsSchema(): AttrsSchema<BoardsSaveButtonAttrs> {
    return {
      ...super.attrsSchema,
      'selected-dashboard-id': `number`,
      'user-id': `number`,
      'can-save-existing': `boolean`,
      'can-save-as-new': `boolean`,
      'align-right': `boolean`,
      tooltip: `string`,
      'search-placeholder': `string`,
      'is-saving': `boolean`,
      'has-unsaved-changes': `boolean`,
      'has-existing-report': `boolean`,
      'show-save-spinner': `boolean`,
    };
  }

  get config() {
    return {
      defaultState: {
        isOpen: false,
        recentDashboardIds: [],
        pendingSaveMode: null,
      },
      contexts: [`currentProject` as const],
      useShadowDom: true,
      css,
      template,
      helpers: {
        handleSaveButtonClicked: () => {
          if (this.attr(`selected-dashboard-id`)) {
            this.dispatchEvent(
              new CustomEvent(`save`, {
                detail: {dashboardId: this.attr(`selected-dashboard-id`), saveMode: SaveModes.Save},
              }),
            );
          } else {
            this.update({isOpen: true, pendingSaveMode: SaveModes.Save});
          }
        },
        handleSaveAsNewButtonClicked: () => {
          if (this.attr(`can-save-existing`) && this.attr(`selected-dashboard-id`)) {
            this.dispatchEvent(
              new CustomEvent(`save`, {
                detail: {dashboardId: this.attr(`selected-dashboard-id`), saveMode: SaveModes.SaveNew},
              }),
            );
          } else {
            this.update({isOpen: true, pendingSaveMode: SaveModes.SaveNew});
          }
        },
        handleDashboardSelected: (ev) => {
          const {pendingSaveMode} = this.state;
          this.update({isOpen: false, pendingSaveMode: null});
          this.dispatchEvent(new CustomEvent(`save`, {detail: {dashboardId: ev.detail.id, saveMode: pendingSaveMode}}));
          ev.preventDefault();
        },
        handleMenuChanged: (ev) => {
          this.update({isOpen: ev.detail.state === `open`});
          this.dispatchEvent(new CustomEvent(`menuchange`, {detail: ev.detail}));
        },
      },
    };
  }

  set dashboardController(controller: DashboardSelectorController) {
    controller.getDashboards();
    this._dashboardController = controller;
    this.update();
  }
  get dashboardController() {
    return this._dashboardController;
  }

  set recentDashboardIds(value) {
    this.update({recentDashboardIds: value});
  }

  get currentProject() {
    return this.getContext(`currentProject`).get().value;
  }

  icon() {
    if (this.fiwFeatureEnabled()) {
      if (this.attr(`show-save-spinner`)) {
        return `spinner`;
      } else if (!this.attr(`has-unsaved-changes`)) {
        return `checkmark`;
      }
    }
    return ``;
  }

  showSaveAsNew() {
    return this.attr(`has-existing-report`) && this.fiwFeatureEnabled();
  }

  isSaveEnabled() {
    return this.attr(`can-save-existing`) && this.attr(`has-unsaved-changes`) && !this.attr(`is-saving`);
  }

  isSaveAsNewEnabled() {
    return this.attr(`can-save-as-new`) && !this.attr(`is-saving`);
  }

  fiwFeatureEnabled() {
    return isFIWFeatureEnabled(this.currentProject);
  }
}

export const BoardsSaveButtonEl = defineAndMakeJsxFactory<BoardsSaveButtonJsxProps>(
  `mp-boards-save-button`,
  BoardsSaveButton,
);
export const BoardsSaveButtonReact = panel2react<BoardsSaveButtonJsxProps>(`mp-boards-save-button`, BoardsSaveButton);
